import React, { Component } from 'react';

import _set from 'lodash.set';
import { bool, string } from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import { FormAPI } from '@philips-emr/tws-components-api';
import { Density } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, element, eventListener, expression, object, oneOf, oneOfType } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, createAbort, isEnumValid, isEqualShallow, isFunction, noop, propagateData, shallowCompare } from '@philips-emr/tws-utils';

import { Button, Link, Loader } from '..';
import './form.scss';

const acceptedTypes = [
  ComponentType.FORM,
];

@AwesomeComponent({ type: ComponentType.FORM, api: FormAPI })
class Form extends Component {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: object.notEnumerable(),
    density: oneOf(Object.values(Density)),
    disabled: bool,
    editing: bool,
    loading: bool,
    locked: bool,
    onBeforeCancel: eventListener,
    onBeforeSubmit: eventListener,
    onCancel: eventListener,
    onChange: eventListener,
    onEdit: eventListener,
    onSubmit: eventListener,
    onValidate: eventListener,
    title: expression,
    uuid: string,
  };

  static defaultProps = {
    data: {},
    density: Density.REGULAR,
    disabled: false,
    editing: false,
    loading: false,
    locked: false,
    onCancel: noop,
    title: '',
  };

  state = {
    innerEditing: this.props.editing,
    record: this.props.data,
    recordChanges: {},
    validationRecord: {},
    forceValidate: false,
    valid: true,
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (shallowCompare(this.props, nextProps, ['children', 'className', 'data', 'density', 'disabled', 'editing', 'loading', 'locked', 'title', 'uuid'])) {
      return true;
    }

    if (shallowCompare(this.state, nextState, ['forceValidate', 'innerEditing', 'record', 'recordChanges', 'validationRecord'])) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps) {
    const { data, editing } = this.props;

    if (prevProps.editing !== editing) {
      this.setState({ innerEditing: editing });
    }

    if (!isEqualShallow(data, prevProps.data)) {
      this.setState({ record: data, recordChanges: {}, forceValidate: true });
    }
  }

  onChange = ({ entry, value }) => {
    const { recordChanges: oldRecordChanges } = this.state;

    const fireOnChange = () => {
      const { onChange } = this.props;
      const { record, recordChanges } = this.state;

      isFunction(onChange) && onChange({
        value: { ...record, ...recordChanges },
        oldValue: { ...record, ...oldRecordChanges },
      });
    };

    if (!entry || !this.state.innerEditing) return;

    this.setState(({ recordChanges = {} }) => ({
      recordChanges: _set({ ...recordChanges }, entry, value),
      forceValidate: false,
    }), fireOnChange);
  };

  onCancel = () => {
    const abort = createAbort();

    const { onBeforeCancel, onCancel } = this.props;
    const { record } = this.state;

    isFunction(onBeforeCancel) && onBeforeCancel({ record, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      isFunction(onCancel) && onCancel({ record, abort: noop });

      this.setState({
        recordChanges: {},
        forceValidate: false,
      });

      this.setEditing(false);
    });
  };

  onSubmit = () => {
    const { valid } = this.state;

    if (!valid) {
      this.setState({ forceValidate: true });
      return;
    }

    const abort = createAbort();

    const { onBeforeSubmit, onSubmit } = this.props;
    const { record, recordChanges } = this.state;

    const newRecord = { ...record, ...recordChanges };

    isFunction(onBeforeSubmit) && onBeforeSubmit({ record: newRecord, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      isFunction(onSubmit) && onSubmit({ record: newRecord, abort: noop });

      this.setState({
        record: newRecord,
        recordChanges: {},
        forceValidate: false,
      });

      this.setEditing(false);
    });
  };

  getRecord() {
    const { record, recordChanges } = this.state;
    return { ...record, ...recordChanges };
  }

  setEditing(value) {
    const { locked } = this.props;

    this.setState(({ innerEditing }) => ({
      innerEditing: locked ? innerEditing : value,
    }));
  }

  toggleEditing = () => {
    const { locked } = this.props;

    if (locked) return;

    const fireOnEdit = () => {
      const { onEdit } = this.props;
      const { innerEditing } = this.state;
      innerEditing && isFunction(onEdit) && onEdit();
    };

    this.setState(({ innerEditing }) => ({
      innerEditing: !innerEditing,
    }), fireOnEdit);
  };

  getFormClassName() {
    const { density } = this.props;

    return classNames({
      'o-form': true,
      [`o-form--${density}`]: isEnumValid(Density, density),
    });
  }

  getClassName() {
    const { className } = this.props;

    return classNames({
      'c-form': true,
      [className]: Boolean(className),
    });
  }

  onValidate = ({ entry, validations = [] }) => {
    const isValid = (validationRecord) => {
      return !Object.values(validationRecord).flat().find(validation => !validation.valid);
    };

    const fireOnValidate = () => {
      const { onValidate } = this.props;
      const { valid } = this.state;

      if (!isFunction(onValidate)) {
        return;
      }

      onValidate({ entry, valid, validations });
    };

    this.setState(({ validationRecord: oldValidationRec }) => {
      const validationRecord =  {
        ...oldValidationRec,
        [entry]: validations,
      };

      const valid = isValid(validationRecord);

      return { validationRecord, valid };
    }, fireOnValidate);
  };

  renderFields() {
    const { density, children } = this.props;
    const { innerEditing, forceValidate } = this.state;
    const record = this.getRecord();

    return propagateData(children, record, {
      className: 'o-form__field',
      density,
      editing: innerEditing,
      onChange: this.onChange,
      onValidate: this.onValidate,
      readOnly: !innerEditing,
      forceValidate,
    });
  }

  renderHeader() {
    const { title, disabled, locked } = this.props;
    const { innerEditing } = this.state;

    if (disabled || (innerEditing && !title.trim())) {
      return null;
    }

    return (
      <div className="c-form__header">
        {title && <span className="c-form__title">{msg(title)}</span>}
        {!innerEditing && !locked && !disabled &&
        <Link className="c-form__edit" onClick={this.toggleEditing}>{msg('tws.form.edit')}</Link>}
      </div>
    );
  }

  renderActions() {
    const { disabled } = this.props;
    const { innerEditing } = this.state;

    if (!innerEditing || disabled) return null;

    return (
      <div className="c-detail__actions">
        <Link className="c-detail__cancel" onClick={this.onCancel}>{msg('tws.form.cancel')}</Link>
        <Button className="c-detail__save" theme="accent" onClick={this.onSubmit}>{msg('tws.form.save')}</Button>
      </div>
    );
  }

  renderLoader() {
    const { loading } = this.props;

    return (
      <CSSTransition
        classNames="c-form__loader--fade"
        in={loading}
        unmountOnExit
        timeout={150}
      >
        <Loader className="c-form__loader" centered={true}></Loader>
      </CSSTransition>
    );
  }

  render() {
    const { uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    return (
      <div className={this.getClassName()}>
        {this.renderHeader()}

        <Container acceptedTypes={acceptedTypes} className="c-form__container" uuid={uuid}>
          <form className={this.getFormClassName()}>
            {this.renderFields()}
          </form>
        </Container>

        {this.renderActions()}

        {this.renderLoader()}
      </div>
    );
  }
}

export default Form;
